import { Box, Paper, Skeleton, Typography } from "@mui/material";
import {
  red,
  orange,
  yellow,
  purple,
  blue,
  teal,
  green,
  cyan,
  indigo,
  pink,
} from "@mui/material/colors";
import { BarChart } from "@mui/x-charts/BarChart";
import { useMemo } from "react";
import { ErrorClusterGroupRow } from "./index";
import { transformToBarChartData } from "./chartUtils";
import type { ChartsAxisContentProps } from "@mui/x-charts/ChartsTooltip";

interface ErrorBarChartProps {
  data: ErrorClusterGroupRow[];
  loading: boolean;
}

const ERROR_CHART_COLORS = [
  red[500],
  orange[500],
  yellow[700],
  purple[500],
  blue[500],
  teal[400],
  green[500],
  cyan[500],
  indigo[400],
  pink[400],
];

export function ErrorBarChart({ data, loading }: ErrorBarChartProps) {
  const chartData = useMemo(() => transformToBarChartData(data), [data]);

  const axisContent = useMemo(() => {
    return (props: ChartsAxisContentProps) => {
      const { dataIndex, axisValue } = props;

      if (dataIndex === undefined || dataIndex === null) {
        return null;
      }

      const fullPattern = chartData.fullCategories[dataIndex];
      const count = chartData.values[dataIndex];

      return (
        <Paper
          elevation={3}
          sx={{
            p: 1.5,
            maxWidth: 400,
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            {fullPattern}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Reports: {count}
          </Typography>
        </Paper>
      );
    };
  }, [chartData]);

  if (loading) {
    return (
      <Paper elevation={0} sx={{ p: 3 }}>
        <Skeleton variant="rectangular" height={500} />
      </Paper>
    );
  }

  if (chartData.categories.length === 0 || chartData.values.length === 0) {
    return (
      <Paper elevation={0} sx={{ p: 3 }}>
        <Typography color="text.secondary">
          No error data available for visualization
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper elevation={0} sx={{ p: 3 }}>
      <Box sx={{ mb: 0 }}>
        <Typography variant="caption" color="text.secondary">
          Showing top 5 error groups
        </Typography>
      </Box>

      <BarChart
        xAxis={[
          {
            scaleType: "band",
            data: chartData.categories,
            label: "Error Groups",
          },
        ]}
        yAxis={[
          {
            label: "Reports",
          },
        ]}
        series={[
          {
            type: "bar",
            data: chartData.values,
          },
        ]}
        height={400}
        margin={{ top: 10, right: 30, bottom: 20, left: 100 }}
        barLabel={(item) => {
          if (item.value === undefined || item.value === null) {
            return "";
          }
          return item.value.toString();
        }}
        slotProps={{
          legend: {
            hidden: true,
          },
          barLabel: {
            style: {
              fontSize: "0.875rem",
              fontWeight: 500,
            },
          },
        }}
        tooltip={{
          trigger: "axis",
        }}
        axisHighlight={{
          x: "band",
        }}
        slots={{
          axisContent,
        }}
        sx={{
          "& .MuiBarElement-root": {
            "&:nth-of-type(1)": { fill: ERROR_CHART_COLORS[0] },
            "&:nth-of-type(2)": { fill: ERROR_CHART_COLORS[1] },
            "&:nth-of-type(3)": { fill: ERROR_CHART_COLORS[2] },
            "&:nth-of-type(4)": { fill: ERROR_CHART_COLORS[3] },
            "&:nth-of-type(5)": { fill: ERROR_CHART_COLORS[4] },
          },
        }}
      />
    </Paper>
  );
}
